import {
  createAppSelector,
  selectCourseUsers,
  selectCurrentCourseId,
  selectCurrentUserId,
  selectOrgUsers,
  selectOrgs,
  selectUsers,
} from "./base";
import { selectCurrentOrgRole, selectOrgRoleByUserId } from "./org";
import { selectPageById } from "./page";
import { selectPageGroupForReadyUser } from "./page-groups";
import {
  selectCourseRoleByCourseId,
  selectCourseRoleByUserId,
  selectCurrentCourseRole,
  selectEnabledCourseUsers,
} from "./section-user";
import { selectIsStudentPreview } from "./view";

import type {
  SectionRoleType,
  SectionUserType,
  OrgRoleType,
  UserType,
} from "components/server-types";
import type { RootState } from "store";
import { isGroupPage } from "utils/page-utils";
import { isMpOrg } from "utils/urls";

export enum UserRoleType {
  ORG_ADMIN = "orgAdmin",
  INSTRUCTOR = "instructor",
  STUDENT = "student",
  COURSE_ADMIN = "course_admin",
  MP_ADMIN = "mpAdmin",
  RECORDING_AGENT = "recordingAgent",
  UNKNOWN = "unknown",
}

// Capabilities at the page level
export enum PageCapability {
  CAN_VIEW_PAGE = "canViewPage",
  CAN_MAKE_RESPONSE = "canMakeResponse",
  CAN_AUTHOR_PAGE = "canAuthorPage",
}

// Capabilities at the course level
export enum CourseCapability {
  CAN_AUTHOR_COURSE = "canAuthorCourse",
  CAN_EDIT_STUDENT_DRAFT = "canEditStudentDraft",
  CAN_CREATE_CHAT_MESSAGE = "canCreateChatMessage",
  CAN_DELETE_CHAT_MESSAGE = "canDeleteChatMessage",
  CAN_PARTICIPATE_IN_COURSE = "canParticipateInCourse",
  CAN_PRESENT_COURSE = "canPresentCourse",
  CAN_START_AND_STOP_RECORDING = "canStartAndStopRecording",
  IS_VISIBLE_IN_CLASS = "isVisibleInClass",
}

// Capabilities at the org level
export enum OrgCapability {
  CAN_ACCESS_MONITORING_DASHBOARD = "canAccessMonitoringDashboard",
  CAN_AUTHOR_ORG = "canAuthorOrg",
  CAN_OBSERVE_VIRTUAL_CLASS = "canObserveVirtualClass",
  CAN_SEE_ALL_DOCS = "canSeeAllDocs",
  CAN_SEE_ALL_ORG_USERS = "canSeeAllOrgUsers",
}

export const Capability = {
  ...CourseCapability,
  ...OrgCapability,
};

const ROLES_MAP: Record<SectionRoleType | "admin" | "recording_agent" | "other", UserRoleType> = {
  admin: UserRoleType.ORG_ADMIN,
  instructor: UserRoleType.INSTRUCTOR,
  course_admin: UserRoleType.COURSE_ADMIN,
  student: UserRoleType.STUDENT,
  recording_agent: UserRoleType.RECORDING_AGENT,
  other: UserRoleType.UNKNOWN,
};

const COURSE_CAPABILITIES_MAP: Record<CourseCapability, UserRoleType[]> = {
  [Capability.CAN_AUTHOR_COURSE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_EDIT_STUDENT_DRAFT]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_CREATE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.STUDENT,
  ],
  [Capability.CAN_DELETE_CHAT_MESSAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.CAN_PARTICIPATE_IN_COURSE]: [UserRoleType.STUDENT],
  // For now, only instructors/course admins can present
  [Capability.CAN_PRESENT_COURSE]: [UserRoleType.INSTRUCTOR, UserRoleType.COURSE_ADMIN],
  [Capability.CAN_START_AND_STOP_RECORDING]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [Capability.IS_VISIBLE_IN_CLASS]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.STUDENT,
  ],
};

const ORG_CAPABILITIES_MAP: Record<OrgCapability, UserRoleType[]> = {
  // TODO: remove org admin capability once mp admins have a none org role
  [Capability.CAN_ACCESS_MONITORING_DASHBOARD]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_AUTHOR_ORG]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
  [Capability.CAN_SEE_ALL_DOCS]: [
    UserRoleType.ORG_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.RECORDING_AGENT,
  ],
  [Capability.CAN_SEE_ALL_ORG_USERS]: [UserRoleType.MP_ADMIN],
  [Capability.CAN_OBSERVE_VIRTUAL_CLASS]: [UserRoleType.ORG_ADMIN, UserRoleType.MP_ADMIN],
};

// Note: for students you need to actually be in the course to view the page so the org role
// does not matter. The only roles that can make a response are students on an individual page
// or are in a group and ready for a group page.
const PAGE_CAPABILITIES_MAP: Record<PageCapability, UserRoleType[]> = {
  [PageCapability.CAN_VIEW_PAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
    UserRoleType.RECORDING_AGENT,
  ],
  [PageCapability.CAN_AUTHOR_PAGE]: [
    UserRoleType.INSTRUCTOR,
    UserRoleType.COURSE_ADMIN,
    UserRoleType.MP_ADMIN,
    UserRoleType.ORG_ADMIN,
  ],
  [PageCapability.CAN_MAKE_RESPONSE]: [], // Only student which we handle separately
};

const getUserRole = (
  isMpAdmin: boolean,
  courseRole: SectionRoleType | null,
  orgRole: OrgRoleType | null,
  isStudentPreview: boolean = false,
): UserRoleType => {
  if (isStudentPreview) {
    return UserRoleType.STUDENT;
  }
  const role = courseRole || orgRole;
  // MP Admin users might have a "none" role in the org, which allows them to have full MP Admin capabilities.
  if (role && role !== "none") {
    return ROLES_MAP[role];
  }
  return isMpAdmin ? UserRoleType.MP_ADMIN : UserRoleType.UNKNOWN;
};

// Course capabilities
export const hasCourseCapability = (userRole: UserRoleType, capability: CourseCapability) =>
  COURSE_CAPABILITIES_MAP[capability].includes(userRole);

const getCourseCapabilities = (userRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(CourseCapability).map((capability) => [
      capability,
      hasCourseCapability(userRole, capability),
    ]),
  ) as Record<CourseCapability, boolean>;
};

// Org capabilities
export const hasOrgCapability = (userOrgRole: UserRoleType, capability: OrgCapability) => {
  return ORG_CAPABILITIES_MAP[capability].includes(userOrgRole);
};

const getOrgCapabilities = (userOrgRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(OrgCapability).map((capability) => [
      capability,
      hasOrgCapability(userOrgRole, capability),
    ]),
  ) as Record<OrgCapability, boolean>;
};

const hasPageCapability = (userRole: UserRoleType, capability: PageCapability) => {
  return PAGE_CAPABILITIES_MAP[capability].includes(userRole);
};

// This is really only useful for non-student roles since student's is specific to page state
const getPageCapabilities = (userRole: UserRoleType) => {
  return Object.fromEntries(
    Object.values(PageCapability).map((capability) => [
      capability,
      hasPageCapability(userRole, capability),
    ]),
  ) as Record<PageCapability, boolean>;
};

// TODO: This should be made more specific, e.g. canReadPage, canWritePage, etc., to avoid
// re-renders when the objects gets updated without causing any actual change.
export const selectCollaborationPermissionsForDoc = (state: RootState, docId: string) =>
  state.auth.websocket.pages?.[docId];

export const selectShouldRequestAuth = (state: RootState) => state.auth.should_request_auth;

export const selectUserIsMpAdmin = createAppSelector(
  [(state, userId: string) => userId, (state) => state.users],
  (userId, users) => users[userId]?.is_superuser,
);

export const selectCurrentUserIsMPAdmin = createAppSelector(
  [selectCurrentUserId, (state) => state.users, selectIsStudentPreview],
  (userId, users, isStudentPreview) => Boolean(!isStudentPreview && users[userId!]?.is_superuser),
);

// Note: using this selector is discouraged, use capability selectors instead
export const selectCurrentUserRole = createAppSelector(
  [
    selectCurrentUserIsMPAdmin,
    selectCurrentCourseRole,
    selectCurrentOrgRole,
    selectIsStudentPreview,
  ],
  (isMpAdmin, courseRole, orgRole, isStudentPreview): UserRoleType =>
    getUserRole(isMpAdmin, courseRole, orgRole, isStudentPreview),
);

export const selectCurrentUserOrgRole = createAppSelector(
  [selectCurrentUserIsMPAdmin, selectCurrentOrgRole],
  (isMpAdmin, orgRole): UserRoleType => getUserRole(isMpAdmin, null, orgRole),
);

export const selectUserRoleByUserId = createAppSelector(
  [selectUserIsMpAdmin, selectCourseRoleByUserId, selectOrgRoleByUserId],
  (isMpAdmin, courseRole, orgRole): UserRoleType => getUserRole(isMpAdmin, courseRole, orgRole),
);

export const selectCurrentUserRoleByCourseId = createAppSelector(
  [selectCurrentUserIsMPAdmin, selectCourseRoleByCourseId, selectCurrentOrgRole],
  (isMpAdmin, courseRole, orgRole): UserRoleType => getUserRole(isMpAdmin, courseRole, orgRole),
);

export const selectIsMPOrg = createAppSelector([selectOrgs], (orgs) =>
  isMpOrg(orgs.find((org) => org.current)),
);

export const selectCourseCapabilitiesByCourseId = createAppSelector(
  [selectCurrentUserRoleByCourseId],
  (userRole) => getCourseCapabilities(userRole),
);

export const selectCourseCapabilities = createAppSelector([selectCurrentUserRole], (userRole) =>
  getCourseCapabilities(userRole),
);

export const selectOrgCapabilities = createAppSelector([selectCurrentUserOrgRole], (userOrgRole) =>
  getOrgCapabilities(userOrgRole),
);

// TODO: We should start using the page capabilities for more than just submission button availability.
export const selectUserPageCapabilities = createAppSelector(
  [
    selectPageById,
    selectCurrentUserRole,
    selectCurrentUserOrgRole,
    (state, pageId: string, worksheetId: string) =>
      selectCollaborationPermissionsForDoc(state, worksheetId),
    (state, pageId: string, worksheetId: string, currentUserId: string) =>
      selectPageGroupForReadyUser(state, pageId, currentUserId),
  ],
  (
    page,
    currentUserRole,
    currentOrgRole,
    permissions,
    readyPageGroup,
  ): Record<PageCapability, boolean> => {
    const { canParticipateInCourse } = getCourseCapabilities(currentUserRole);
    const { canSeeAllDocs } = getOrgCapabilities(currentOrgRole);
    if (canParticipateInCourse) {
      const hasPagePermissions = Boolean(permissions) || canSeeAllDocs;

      // If it's a group page user has to be in a group and ready to make a response
      const canUpdatePage = Boolean(
        hasPagePermissions && isGroupPage(page) ? readyPageGroup : hasPagePermissions,
      );

      const canMakeResponse = canUpdatePage && !page?.locked_at;

      return {
        [PageCapability.CAN_VIEW_PAGE]: hasPagePermissions,
        [PageCapability.CAN_MAKE_RESPONSE]: canMakeResponse,
        [PageCapability.CAN_AUTHOR_PAGE]: false,
      };
    }

    return getPageCapabilities(currentUserRole);
  },
);

export const selectCourseCapabilitiesByUserId = createAppSelector(
  [selectUserRoleByUserId],
  (userRole) => getCourseCapabilities(userRole),
);

export const selectIsRecordingAgent = createAppSelector(
  [selectCurrentUserRole],
  (userRole) => userRole === UserRoleType.RECORDING_AGENT,
);

const getCourseUsersWithCourseCapability = (
  courseUsers: SectionUserType[],
  capability: CourseCapability,
  courseId: string | null,
) => {
  if (!courseId) {
    return [];
  }
  return courseUsers.filter((courseUser) => {
    if (courseUser.section_id !== courseId) {
      return false;
    }
    const courseRole = courseUser.role;
    return hasCourseCapability(ROLES_MAP[courseRole], capability);
  });
};

export const getCourseUserIdsWithCourseCapability = (
  courseUsers: SectionUserType[],
  capability: CourseCapability,
  courseId: string | null,
) => getCourseUsersWithCourseCapability(courseUsers, capability, courseId).map(({ id }) => id);

export const selectCourseUserIdsWithCourseCapability = createAppSelector(
  [
    selectEnabledCourseUsers,
    selectCurrentCourseId,
    (state, capability: CourseCapability) => capability,
  ],
  (courseUsers, courseId, capability) =>
    getCourseUserIdsWithCourseCapability(courseUsers, capability, courseId),
);

// User role map in the current course
// This is used to determine who has what capabilities in the current course.
// TODO: consider using selectEnabledCourseUsers for this
export const selectUserRoleMapInCurrentCourse = createAppSelector(
  [selectUsers, selectCourseUsers, selectOrgUsers, selectCurrentCourseId],
  (users, courseUsers, orgUsers, courseId): Array<[UserType, UserRoleType]> => {
    // Users with a role in the course include:
    // - Course users with a role in the course
    // - Org users with a role in the org other than "none"
    // - Users with a system-wide role
    // but does not include disabled users at the course or org level

    const courseUsersByUserId = new Map(
      courseUsers.filter((cu) => cu.section_id === courseId).map((cu) => [cu.user_id, cu]),
    );
    const orgUsersByUserId = new Map(orgUsers.map((ou) => [ou.user_id, ou]));
    const enabledUsers = users.filter((user) => {
      const courseUser = courseUsersByUserId.get(user.id);
      if (courseUser && courseUser.is_disabled) {
        return false;
      }

      const orgUser = orgUsersByUserId.get(user.id);
      if (orgUser && orgUser.is_disabled && orgUser.role !== "none") {
        return false;
      }

      return true;
    });

    return enabledUsers.map((user) => [
      user,
      getUserRole(
        user.is_superuser,
        courseUsersByUserId.get(user.id)?.role || null,
        orgUsersByUserId.get(user.id)?.role || null,
        false,
      ),
    ]);
  },
);

// User with specific capabilities in the current course

const getUsersWithCourseCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: CourseCapability,
) => {
  return userRoleMap
    .filter(([, role]) => hasCourseCapability(role, capability))
    .map(([user]) => user);
};

export const selectUsersWhoCanParticipateInCourse = createAppSelector(
  [selectUserRoleMapInCurrentCourse],
  (userRoleMap) => getUsersWithCourseCapability(userRoleMap, Capability.CAN_PARTICIPATE_IN_COURSE),
);

// User IDs with specific capabilities in the current course

const getUserIdsWithCourseCapability = (
  userRoleMap: Array<[UserType, UserRoleType]>,
  capability: CourseCapability,
) => {
  return getUsersWithCourseCapability(userRoleMap, capability).map((user) => user.id);
};

export const selectUserIdsWhoAreVisibleInClass = createAppSelector(
  [selectUserRoleMapInCurrentCourse],
  (userRoleMap) => getUserIdsWithCourseCapability(userRoleMap, Capability.IS_VISIBLE_IN_CLASS),
);

export const selectUserIdsWhoCanAuthorCourse = createAppSelector(
  [selectUserRoleMapInCurrentCourse],
  (userRoleMap) => getUserIdsWithCourseCapability(userRoleMap, Capability.CAN_AUTHOR_COURSE),
);

export const selectUserIdsWhoCanCreateChatMessage = createAppSelector(
  [selectUserRoleMapInCurrentCourse],
  (userRoleMap) => getUserIdsWithCourseCapability(userRoleMap, Capability.CAN_CREATE_CHAT_MESSAGE),
);

export const selectUserIdsWhoCanParticipateInCourse = createAppSelector(
  [selectUserRoleMapInCurrentCourse, selectEnabledCourseUsers],
  (userRoleMap, enabledCourseUsers) => {
    const studentUserIds = getUserIdsWithCourseCapability(
      userRoleMap,
      Capability.CAN_PARTICIPATE_IN_COURSE,
    );

    // Filter out disabled course users
    return studentUserIds.filter((user_id) =>
      enabledCourseUsers.some((cu) => cu.user_id === user_id),
    );
  },
);

// User counts with specific capabilities in the current course

export const selectCountUsersWhoCanParticipateInCourse = createAppSelector(
  [selectUserIdsWhoCanParticipateInCourse],
  (userIds) => userIds.length,
);
